// LanguageContext.tsx
"use client";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { defaultLocale, normalizeLocale, type Locale } from "@/lib/i18n/locales";

export type Language = Locale;

const STORAGE_KEY = "preferredLang";

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  /**
   * Apply a tenant-derived default language. Only takes effect when the user
   * has not chosen a language explicitly. Does not persist to localStorage;
   * the user's explicit choice remains the source of truth.
   */
  applyTenantDefault: (lang: string) => void;
  /** Clear the explicit user override. */
  clearUserOverride: () => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: defaultLocale,
  setLanguage: () => {},
  applyTenantDefault: () => {},
  clearUserOverride: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(defaultLocale);

  useEffect(() => {
    const saved = normalizeLocale(localStorage.getItem(STORAGE_KEY));
    if (saved) {
      setLanguageState(saved);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const applyTenantDefault = useCallback((lang: string) => {
    if (typeof window === "undefined") return;
    // Only a *valid* stored locale counts as an explicit user choice; stale or
    // corrupted values must not block the tenant default forever.
    if (normalizeLocale(localStorage.getItem(STORAGE_KEY))) {
      return; // user has an explicit choice; respect it
    }
    const normalized = normalizeLocale(lang);
    if (normalized) {
      setLanguageState(normalized);
    }
  }, []);

  const clearUserOverride = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, applyTenantDefault, clearUserOverride }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
